import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { buildSehatPayload, buildSehatDraftPayload, sehatService } from "@/services/mukamal-sehat/sehatService";
import { saveForLaterService } from "@/services/saveForLaterService";

// ─────────────────────────────────────────────────────────────────────────────
// ASYNC THUNK — Submit final policy purchase to API
// Assembled here from the complete state so no component needs to gather data.
// ─────────────────────────────────────────────────────────────────────────────
export const submitMukamalSehatPolicy = createAsyncThunk(
    "mukamalSehat/submitPolicy",
    async (_, { getState, rejectWithValue }) => {
        const { plan, members, payment } = getState().mukamalSehat;
        const user = getState().auth?.user ?? {};
        try {
            const payload = buildSehatPayload({ plan, members, payment, user });
            const data = await sehatService.submitPolicy(payload);
            return data;

        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message ||
                err.response?.data ||
                err.message ||
                "Submission failed"
            );
        }
    }
);

// ─────────────────────────────────────────────────────────────────────────────
// ASYNC THUNK — Save policy draft for later
// Prepares draft data using buildSehatPayload and saves via saveForLaterService
// ─────────────────────────────────────────────────────────────────────────────
export const saveMukamalSehatDraft = createAsyncThunk(
    "mukamalSehat/saveDraft",
    async (_, { getState, rejectWithValue }) => {
        const { plan, members, payment } = getState().mukamalSehat;
        const user = getState().auth?.user ?? {};
        const draftId = getState().termLife?.draftId || null;
        try {
            const payload = buildSehatDraftPayload({ plan, members, payment, user, draftId });
            const data = await saveForLaterService.saveDraft(payload);
            if (!data?.success) {
                return rejectWithValue(data?.error || data?.message || "Failed to save draft");
            }
            return data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data?.message ||
                err.response?.data ||
                err.message ||
                "Failed to save draft"
            );
        }
    }
);

// ─────────────────────────────────────────────────────────────────────────────
// MEMBER TEMPLATES
// Re-used for reset and for empty child entries.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fields present on every dependent member (spouse / father / mother / child).
 * These are split into two groups:
 *   • Pricing fields  – filled during SehatPricingQuote (Step 1)
 *   • Identity fields – filled during DependentForm      (Step 2)
 *
 * relation: the text the user types in DependentForm (e.g. "Wife", "Son", "Father").
 *           It is kept here rather than hard-coded so the user's exact input is
 *           preserved and sent to the API.
 */
const emptyDependent = () => ({
    // Pricing (Step 1)
    dob: "",
    premium: 0,

    // Identity (Step 2)
    fullName: "",
    relation: "",       // user-typed in DependentForm
    maritalStatus: "",
    cnic: "",
    cnicFrontImage: null, // { name, base64, type } – stored as Base64 for serialisability
    cnicBackImage: null,
    bFormImage: null,     // children only – B-Form document
});

// ─────────────────────────────────────────────────────────────────────────────
// INITIAL STATE
// ─────────────────────────────────────────────────────────────────────────────
const initialState = {

    // ── THE PLAN ──────────────────────────────────────────────────────────────
    // Everything describing what is being purchased.
    // Enriched across two UI screens (PlanSelection → SehatPricingQuote).
    plan: {
        // From PlanSelection (Step 1a)
        packageTitle: "",         // "Bronze" | "Silver" | "Gold" | "Platinum" | "Titanium"
        annualCoverage: 0,        // raw number, e.g. 500000
        packageIcon: "",          // SVG path from sehatPackages

        // From SehatPricingQuote (Step 1b)
        planType: "",             // "Personal" | "Family"
        addOns: [],               // [{ key, title, price }]  – selected add-ons
        basePremium: 0,           // plan premium excluding add-ons (PKR)
        totalPremium: 0,          // final calculated annual premium (PKR)
    },

    // ── THE MEMBERS ───────────────────────────────────────────────────────────
    // Normalised by role. Each member's data is enriched from two screens:
    //   Step 1 (SehatPricingQuote)  → dob, premium
    //   Step 2 (PersonalDetails)    → identity + document images
    //
    // This means DependentForm can pre-fill dateOfBirth from the DOB already
    // saved here from Step 1, giving the user a seamless experience.
    members: {

        // ── Self (Policy Holder) ─────────────────────────────────────────────────
        // DOB comes from auth.user.date_of_birth (read-only in Step 1).
        // Identity fields are filled in PolicyHolderForm (Step 2).
        self: {
            // Pricing (Step 1)
            dob: "",
            premium: 0,
            // Set once OCR (Step 2, PolicyHolderForm) has verified the DOB from
            // the CNIC — locks the Step 1 field the same way a profile-sourced
            // DOB already does.
            dobVerified: false,

            // Identity (Step 2) — PolicyHolderForm
            fullName: "",
            email: "",
            residentialAddress: "",
            city: "",
            maritalStatus: "",
            cnic: "",
            cnicIssuanceDate: "",
            cnicExpiryDate: "",
            // Set once OCR has verified the issuance/expiry dates from the CNIC —
            // locks those fields in PolicyHolderForm the same way dobVerified does.
            cnicDatesVerified: false,
            cnicFrontImage: null,   // { name, base64, type }
            cnicBackImage: null,
        },

        // ── Spouse ───────────────────────────────────────────────────────────────
        // isActive mirrors the "showSpouse" toggle in SehatPricingQuote.
        // relation: e.g. "Wife" or "Husband" — typed by user in DependentForm.
        spouse: {
            isActive: false,
            ...emptyDependent(),
        },

        // ── Children ─────────────────────────────────────────────────────────────
        // Dynamic array. Each element uses emptyDependent() shape.
        // Added / removed in SehatPricingQuote; identity filled in DependentForm.
        // Index-stable: child at index 0 always maps to the first added child.
        // WARNING: if the user removes a child from the middle in Step 1 and
        //          returns to Step 2, indices shift. Children are matched by
        //          position — a full re-save of pricing always reconciles this.
        children: [],
        // Each child element shape (same as emptyDependent()):
        // { dob, premium, fullName, relation, maritalStatus, cnic, bFormImage }

        // ── Father ───────────────────────────────────────────────────────────────
        // Personal Shield only. isActive mirrors showFather in SehatPricingQuote.
        // relation: typically "Father" but user can override.
        father: {
            isActive: false,
            ...emptyDependent(),
        },

        // ── Mother ───────────────────────────────────────────────────────────────
        mother: {
            isActive: false,
            ...emptyDependent(),
        },
    },

    // ── PAYMENT PREFERENCES ───────────────────────────────────────────────────
    // Captured in SehatPayment (Step 3) before confirmation.
    // Restored when user navigates back to that screen.
    payment: {
        selectedNgo: "",      // key from ngos map, e.g. "edhi"
        coinDiscount: 0,      // PKR amount discounted via coin redemption
        isRedeemed: false,    // whether coins have been applied
    },

    // ── API STATE ─────────────────────────────────────────────────────────────
    loading: false,
    error: null,
    submissionResponse: null,
};

// ─────────────────────────────────────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────────────────────────────────────
const mukamalSehatSlice = createSlice({
    name: "mukamalSehat",
    initialState,
    reducers: {

        // ── Landing Form Pre-fill ─────────────────────────────────────────────────
        // Dispatched from PublicProductForm1 (the public product landing page).
        // Pre-populates plan type, policy holder's name and DOB before the user
        // reaches the stepper, so the stepper forms can be pre-filled.
        // planType must be "Personal" | "Family" (mapped from the landing form values).
        //
        // IMPORTANT: packageTitle and packageIcon are intentionally cleared here so
        // that PlanSelection always shows the package selection screen first for a
        // landing journey — even if the user had a previous completed session in Redux.
        setLandingFormData(state, { payload }) {
            const { planType, fullName, dob } = payload;
            state.plan.planType = planType;
            state.plan.packageTitle = "";
            state.plan.packageIcon = "";
            state.members.self.fullName = fullName;
            state.members.self.dob = dob;
        },

        // ── Step 1a: PlanSelection ────────────────────────────────────────────────
        // Dispatched when user clicks a package tile and clicks "Proceed".
        // Saves the three display fields from sehatPackages entry.
        setPackage(state, { payload }) {
            state.plan.packageTitle = payload.title;
            state.plan.annualCoverage = payload.annual_coverage;
            state.plan.packageIcon = payload.icon;
        },

        // ── Step 1b: SehatPricingQuote ────────────────────────────────────────────
        // Dispatched when user confirms the pricing screen ("Buy Now" → confirm modal).
        // Saves plan-level selections + pricing data for every member.
        //
        // Payload shape:
        // {
        //   planType, addOns, totalPremium,
        //   self:     { dob, premium },
        //   spouse:   { isActive, dob, premium },
        //   children: [{ dob, premium }, ...],
        //   father:   { isActive, dob, premium },
        //   mother:   { isActive, dob, premium },
        // }
        setPlanAndMembers(state, { payload }) {
            const { planType, addOns, basePremium, totalPremium,
                self, spouse, children, father, mother } = payload;

            // Plan fields
            state.plan.planType = planType;
            state.plan.addOns = addOns;
            state.plan.basePremium = basePremium ?? 0;
            state.plan.totalPremium = totalPremium;

            // Self — pricing fields only; never overwrite identity
            state.members.self.dob = self.dob;
            state.members.self.premium = self.premium;

            // Spouse — pricing fields; preserve existing identity
            state.members.spouse.isActive = spouse.isActive;
            state.members.spouse.dob = spouse.isActive ? spouse.dob : "";
            state.members.spouse.premium = spouse.isActive ? spouse.premium : 0;

            // Children — rebuild array but preserve identity fields by index.
            // If the user modifies children in Step 1 after already filling Step 2,
            // identity data at the same index position is carried forward.
            const existingChildren = state.members.children;
            state.members.children = children.map((c, i) => ({
                // New pricing data from the form
                dob: c.dob,
                premium: c.premium,
                displayAge: c.displayAge ?? "",
                // Preserved identity (or blank if this index is new)
                fullName: existingChildren[i]?.fullName ?? "",
                relation: existingChildren[i]?.relation ?? "",
                maritalStatus: existingChildren[i]?.maritalStatus ?? "",
                cnic: existingChildren[i]?.cnic ?? "",
                cnicFrontImage: null,   // CNIC not applicable for children
                cnicBackImage: null,
                bFormImage: existingChildren[i]?.bFormImage ?? null,
            }));

            // Father — pricing fields; preserve existing identity
            state.members.father.isActive = father.isActive;
            state.members.father.dob = father.isActive ? father.dob : "";
            state.members.father.premium = father.isActive ? father.premium : 0;

            // Mother
            state.members.mother.isActive = mother.isActive;
            state.members.mother.dob = mother.isActive ? mother.dob : "";
            state.members.mother.premium = mother.isActive ? mother.premium : 0;
        },

        // ── Step 2a: PolicyHolderForm ─────────────────────────────────────────────
        // Dispatched when self (policy holder) completes their identity form.
        // Merges only identity fields — never touches dob or premium.
        setSelfIdentity(state, { payload }) {
            const {
                fullName, email, residentialAddress, city,
                maritalStatus, cnic, cnicIssuanceDate, cnicExpiryDate, cnicDatesVerified,
                cnicFrontImage, cnicBackImage,
            } = payload;
            Object.assign(state.members.self, {
                fullName,
                email,
                residentialAddress,
                city,
                maritalStatus,
                cnic,
                cnicIssuanceDate,
                cnicExpiryDate,
                cnicDatesVerified: cnicDatesVerified ?? state.members.self.cnicDatesVerified,
                cnicFrontImage,   // { name, base64, type } or null
                cnicBackImage,
            });
        },

        // ── Step 2a (OCR): PolicyHolderForm ──────────────────────────────────────
        // Dispatched when CNIC OCR extracts a DOB for the policy holder. This is
        // the one place self.dob is touched outside Step 1 — deliberately
        // separate from setSelfIdentity, and paired with a premium recalculation
        // (whatever DOB Step 1 priced the plan with may now be wrong) plus
        // dobVerified so Step 1's field locks against further manual edits.
        setSelfDobFromOcr(state, { payload }) {
            const { dob, premium, basePremium, totalPremium } = payload;
            state.members.self.dob = dob;
            state.members.self.dobVerified = true;
            if (premium != null) state.members.self.premium = premium;
            if (basePremium != null) state.plan.basePremium = basePremium;
            if (totalPremium != null) state.plan.totalPremium = totalPremium;
        },

        // ── Step 2b: DependentForm ────────────────────────────────────────────────
        // Dispatched when user confirms a single dependent's identity form.
        //
        // memberId:
        //   "spouse"      → state.members.spouse
        //   "children.0"  → state.members.children[0]
        //   "children.1"  → state.members.children[1]  ...etc
        //   "father"      → state.members.father
        //   "mother"      → state.members.mother
        //
        // data shape: { fullName, relation, maritalStatus, cnic,
        //               cnicFrontImage, cnicBackImage, bFormImage }
        // (components pass only the fields relevant to their member type)
        setDependentIdentity(state, { payload: { memberId, data } }) {
            if (memberId === "spouse") {
                Object.assign(state.members.spouse, data);
            } else if (memberId.startsWith("children.")) {
                const idx = parseInt(memberId.split(".")[1], 10);
                if (state.members.children[idx]) {
                    Object.assign(state.members.children[idx], data);
                }
            } else if (memberId === "father") {
                Object.assign(state.members.father, data);
            } else if (memberId === "mother") {
                Object.assign(state.members.mother, data);
            }
        },

        // ── Step 3: SehatPayment ──────────────────────────────────────────────────
        // Dispatched when user selects NGO / redeems coins, before confirming payment.
        // Restored when user navigates back to the payment screen.
        setPaymentPreferences(state, { payload }) {
            state.payment.selectedNgo = payload.selectedNgo;
            state.payment.coinDiscount = payload.coinDiscount;
            state.payment.isRedeemed = payload.isRedeemed;
        },

        // ── Full Reset ────────────────────────────────────────────────────────────
        // Dispatched from the Cancel button in TermLifeSignUpClient.
        resetMukamalSehatData: () => initialState,
    },

    extraReducers: (builder) => {
        builder
            .addCase(submitMukamalSehatPolicy.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(submitMukamalSehatPolicy.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.submissionResponse = payload;
            })
            .addCase(submitMukamalSehatPolicy.rejected, (state, { payload }) => {
                state.loading = false;
                state.error = payload;
            });
    },
});

export const {
    setPackage,
    setLandingFormData,
    setPlanAndMembers,
    setSelfIdentity,
    setSelfDobFromOcr,
    setDependentIdentity,
    setPaymentPreferences,
    resetMukamalSehatData,
} = mukamalSehatSlice.actions;

export default mukamalSehatSlice.reducer;

// ─────────────────────────────────────────────────────────────────────────────
// SELECTORS
// ─────────────────────────────────────────────────────────────────────────────
export const selectSehatPlan = (state) => state.mukamalSehat.plan;
export const selectSehatMembers = (state) => state.mukamalSehat.members;
export const selectSehatPayment = (state) => state.mukamalSehat.payment;
export const selectSehatLoading = (state) => state.mukamalSehat.loading;
export const selectSehatError = (state) => state.mukamalSehat.error;

/**
 * Returns an ordered array of active dependents (all members except self),
 * each annotated with a stable memberId string.
 * Used by DependentInformation to render the dependent list.
 *
 * Shape of each item:
 * {
 *   memberId: "spouse" | "children.0" | "father" | "mother",
 *   dob, premium, fullName, relation, maritalStatus, cnic,
 *   cnicFrontImage, cnicBackImage, bFormImage,
 *   isIdentityComplete: bool   ← true when fullName has been saved
 * }
 */
export const selectActiveDependents = (state) => {
    const { spouse, children, father, mother } = state.mukamalSehat.members;
    const list = [];

    if (spouse.isActive && spouse.dob) {
        list.push({
            memberId: "spouse",
            ...spouse,
            isIdentityComplete: !!spouse.fullName,
        });
    }

    if (father.isActive && father.dob) {
        list.push({
            memberId: "father",
            ...father,
            isIdentityComplete: !!father.fullName,
        });
    }

    if (mother.isActive && mother.dob) {
        list.push({
            memberId: "mother",
            ...mother,
            isIdentityComplete: !!mother.fullName,
        });
    }

    children.forEach((child, i) => {
        if (child.dob) {
            list.push({
                memberId: `children.${i}`,
                ...child,
                isIdentityComplete: !!child.fullName,
            });
        }
    });

    return list;
};
